export interface User {
  id: number;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  owner: number | null;
  role?: "admin" | "user";
  createdAt?: string;
  updatedAt?: string;
}

export interface FileProperties {
  id: number;
  name: string;
  relativePath: string;
  thumbnailPath40: string;
  thumbnailPath110: string;
  thumbnailPath280: string;
}

export interface ContactInfo {
  id: number;
  name: string;
  email: string;
  phone: string;
  phone2: string;
}

export interface Staging {
  id: number;
  address: string;
  status: number;
  notes: string;
  startDate: string | null;
  endDate: string | null;
  image: number | null;
  ownerContact: number | null;
  createdAt?: string;
  updatedAt?: string;
  rooms?: Room[];
  imageFile?: FileProperties | null;
  ownerContactInfo?: ContactInfo | null;
}

export interface StagingSummary {
  id: number;
  address: string;
  status: number;
  notes: string;
  startDate: string | null;
  endDate: string | null;
  image: FileProperties | null;
  ownerContact: ContactInfo | null;
  onSiteItems: number;
  lockerItems: number;
  itemsAtLocationCount: number;
  atOtherLocation: number;
}

export interface Room {
  id: number;
  staging: number;
  name: string;
  notes: string;
  orderIndex: number;
  image: number | null;
  templateAssociation: number | null;
  furnitureItems?: FurnitureItem[];
  furnitureItemHistory?: FurnitureItem[];
  stagingInfo?: Staging | null;
  templateAssoc?: RoomGenericItemAssociation | null;
}

export interface RoomGenericItem {
  id: number;
  name: string;
  notes: string;
  quantity: number;
  orderIndex: number;
  checked: boolean;
  replaceItem: number | null;
  roomAssociation: number;
  furnitureCategory: number | null;
  replaceItemInfo?: FurnitureItem | null;
  categoryInfo?: FurnitureCategory | null;
}

export interface RoomGenericItemAssociation {
  id: number;
  room: number;
  template: number | null;
  items?: RoomGenericItem[];
}

export interface Furniture {
  id: number;
  name: string;
  category: number | null;
  supplier: string;
  length: number | null;
  width: number | null;
  height: number | null;
  notes: string;
  image: number | null;
  categoryInfo?: FurnitureCategory | null;
  imageFile?: FileProperties | null;
  tags?: FurnitureTag[];
  items?: FurnitureItem[];
}

export interface FurnitureCategory {
  id: number;
  name: string;
}

export interface FurnitureTag {
  id: number;
  name: string;
}

export interface FurnitureItem {
  id: number;
  furniture: number;
  status: number;
  room: number | null;
  roomReservation: number | null;
  storageLocation: number | null;
  purchaseDate: string | null;
  purchaseCost: number | null;
  notes: string;
  roomChecked: boolean;
  roomNotes: string;
  furnitureInfo?: Furniture | null;
  locationInfo?: StorageLocation | null;
  roomInfo?: Room | null;
  reservationInfo?: Room | null;
}

export interface FurnitureReservationHistoryEntry {
  id: number;
  furnitureItem: number;
  reservedAt: string;
  roomInfo?: Room | null;
}

export interface StorageCategory {
  id: number;
  name: string;
  isInOrganizedStorage: boolean;
}

export interface StorageLocation {
  id: number;
  category: number | null;
  bin: string;
  slot: string;
  categoryInfo?: StorageCategory | null;
}

export interface Template {
  id: number;
  name: string;
  items?: GenericItem[];
}

export interface GenericItem {
  id: number;
  name: string;
  quantity: number;
  template: number | null;
  furnitureCategory: number | null;
  categoryInfo?: FurnitureCategory | null;
}

export interface Note {
  id: number;
  text: string;
  user: number | null;
  createdAt: string;
  userInfo?: User | null;
}

export interface FileUploadResult {
  id: number;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrls: string[];
  createdAt: string;
}

export const STAGING_STATUS = {
  0: "Upcoming",
  1: "Staging",
  2: "Sold",
  3: "Destaging",
  4: "Completed",
} as const;

export const FURNITURE_STATUS = {
  0: "Serviceable",
  1: "Needs repair (Available)",
  2: "Needs repair (Unavailable)",
  3: "Beyond repair (Unavailable)",
  4: "Obsolete",
} as const;

export enum FurnitureStatus {
  Serviceable,
  NeedsRepairAvailable,
  NeedsRepairUnavailable,
  BeyondRepairUnavailable,
  Obsolete,
}

export enum StagingStatus {
  Upcoming,
  Staging,
  Sold,
  Destaging,
  Completed,
}
